import React, { useEffect, useState } from 'react';
import { api, fmtDate } from '../utils/api';
import Topbar from '../components/Topbar';
import { EmptyState, Spinner, SearchInput, useToast, Modal } from '../components/UI';

export default function Activity({ user }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showToast, Toast] = useToast();
  const [detailModal, setDetailModal] = useState(null);
  const isAdmin = user?.role === 'admin';

  const load = () => {
    api.getActivity().then(a => { setLog(a || []); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const filtered = log.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.action||'').toLowerCase().includes(q) || (l.details||'').toLowerCase().includes(q) || (l.adminName||'').toLowerCase().includes(q);
  });

  // Group by date
  const grouped = {};
  filtered.forEach(l => {
    const date = l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' }) : 'Unknown';
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(l);
  });

  const actionIcons = {
    'Approved': 'ti-check', 'Rejected': 'ti-x', 'Created': 'ti-plus', 'Deleted': 'ti-trash',
    'Edited': 'ti-edit', 'Updated': 'ti-refresh', 'Login': 'ti-login', 'Profit': 'ti-trending-up',
    'Royalty': 'ti-coin', 'Upload': 'ti-upload', 'Amendment': 'ti-git-merge', 'Xero': 'ti-file-invoice',
  };
  const actionColors = {
    'Approved': 'var(--green)', 'Rejected': 'var(--red)', 'Deleted': 'var(--red)',
    'Created': 'var(--blue)', 'Profit': 'var(--green)', 'Xero': 'var(--navy)',
  };

  function getIcon(action) {
    const key = Object.keys(actionIcons).find(k => (action||'').startsWith(k));
    return key ? actionIcons[key] : 'ti-activity';
  }
  function getColor(action) {
    const key = Object.keys(actionColors).find(k => (action||'').startsWith(k));
    return key ? actionColors[key] : 'var(--text-secondary)';
  }

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Activity" /><Spinner /></div>;

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Activity Log" subtitle={`${log.length} entries`} actions={
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => {
            const csv = ['Date,Admin,Action,Details'].concat(log.map(l => `"${fmtDate(l.createdAt)}","${l.adminName||''}","${l.action||''}","${(l.details||'').replace(/"/g,'""')}"`)).join('\n');
            const a = document.createElement('a'); a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv);
            a.download='activity-log.csv'; a.click();
          }}>
            <i className="ti ti-download" style={{ fontSize:13 }} /> Export
          </button>
          {isAdmin && (
            <button className="btn btn-danger btn-sm" onClick={async () => {
              if (!window.confirm('Clear all activity log entries? This cannot be undone.')) return;
              await api.clearActivity();
              showToast('Log cleared'); load();
            }}>
              <i className="ti ti-trash" style={{ fontSize:13 }} /> Clear Log
            </button>
          )}
        </div>
      } />
      {Toast}

      <div style={{ padding:24 }}>
        <div style={{ marginBottom:16, maxWidth:380 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search activity..." />
        </div>

        {filtered.length === 0 ? (
          <div className="card" style={{ padding:48 }}><EmptyState icon="ti-activity" message="No activity found" /></div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
            {Object.entries(grouped).map(([date, entries]) => (
              <div key={date}>
                <div style={{ padding:'10px 0', fontSize:11, fontWeight:600, color:'var(--text-tertiary)', letterSpacing:'0.07em', textTransform:'uppercase', borderBottom:'1px solid var(--border)', marginBottom:0 }}>
                  {date}
                </div>
                <div className="card" style={{ overflow:'hidden', marginBottom:12 }}>
                  {entries.map((l, i) => (
                    <div key={l.id} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 16px', borderBottom:i<entries.length-1?'1px solid var(--border)':undefined, cursor:'pointer', transition:'background 0.1s' }}
                      onClick={() => setDetailModal(l)}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                        <i className={`ti ${getIcon(l.action)}`} style={{ fontSize:14, color:getColor(l.action) }} />
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                          <div>
                            <span style={{ fontWeight:600, fontSize:13 }}>{l.action}</span>
                            {l.adminName && <span style={{ fontSize:12, color:'var(--text-tertiary)', marginLeft:8 }}>by {l.adminName}</span>}
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:11, color:'var(--text-tertiary)', flexShrink:0 }}>
                              {l.createdAt ? new Date(l.createdAt).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}
                            </span>
                            <i className="ti ti-chevron-right" style={{ fontSize:13, color:'var(--text-tertiary)' }} />
                          </div>
                        </div>
                        {l.details && <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:2 }}>{l.details}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && (
        <Modal title="Activity Detail" onClose={() => setDetailModal(null)}
          footer={<button className="btn btn-secondary" onClick={() => setDetailModal(null)}>Close</button>}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <i className={`ti ${getIcon(detailModal.action)}`} style={{ fontSize:18, color:getColor(detailModal.action) }} />
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:'var(--navy)' }}>{detailModal.action}</div>
                {detailModal.adminName && <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>by {detailModal.adminName}</div>}
              </div>
            </div>
            {detailModal.details && (
              <div><label>DETAILS</label><div style={{ fontSize:13, color:'var(--text-secondary)', background:'var(--bg)', padding:'10px 14px', borderRadius:'var(--r-md)' }}>{detailModal.details}</div></div>
            )}
            {detailModal.createdAt && (
              <div><label>DATE & TIME</label><div style={{ fontSize:13 }}>{new Date(detailModal.createdAt).toLocaleString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' })}</div></div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
